import React, { useState } from "react";
import { Newspaper, ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";
import EditableText from "./EditableText";
import { Dialog, DialogContent } from "./ui/dialog";
import { notableActivities } from "../mock";

const NotableActivities = () => {
  const [idx, setIdx] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const total = notableActivities.length;
  const item = notableActivities[idx];
  const hasImage = Boolean(item.img);

  const prev = () => setIdx((i) => (i - 1 + total) % total);
  const next = () => setIdx((i) => (i + 1) % total);

  return (
    <section id="activities" className="w-full bg-[#ecd0bc] py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-6">
        <EditableText id="activities.title" as="h2"
            className="text-center block text-[#1a1a1a] font-semibold mb-10"
            style={{ fontFamily: "'Quicksand', system-ui, sans-serif", fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}>
          Notable Trust Activities
        </EditableText>

        <div className="relative rounded-2xl shadow-xl overflow-hidden bg-[#7d8f4f]">
          <div className="p-4 sm:p-6 md:p-10">
            <div className="bg-[#f5ecd6] rounded-md shadow-inner overflow-hidden">
              <div className={hasImage ? "grid md:grid-cols-[1.15fr_1fr]" : "grid grid-cols-1"}>
                {hasImage && (
                  <button
                    onClick={() => setZoomed(true)}
                    className="group relative bg-[#e6dcc2] border-b md:border-b-0 md:border-r border-[#c9bea4] overflow-hidden focus:outline-none"
                    aria-label="View full newspaper clipping"
                  >
                    <img
                      src={item.img}
                      alt={item.title}
                      className="w-full h-[280px] sm:h-[360px] md:h-[480px] object-contain bg-[#faf2df]"
                      draggable={false}
                    />
                    <div className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <ZoomIn size={16} />
                    </div>
                  </button>
                )}
                <div className="p-6 md:p-8 min-h-[240px] md:min-h-[480px] flex flex-col">
                  <div className="flex items-center gap-2 text-[#5a4530] mb-3">
                    <Newspaper size={18} />
                    <EditableText id={`activity.${item.id}.paper`} as="span" className="text-xs uppercase tracking-widest font-semibold">
                      {item.paper}
                    </EditableText>
                  </div>
                  <EditableText id={`activity.${item.id}.title`} as="h3"
                      className="text-[#2a2a2a] text-xl md:text-2xl font-semibold mb-4 block"
                      style={{ fontFamily: "'Quicksand', system-ui, sans-serif" }}>
                    {item.title}
                  </EditableText>
                  <EditableText id={`activity.${item.id}.summary`} as="p" multiline
                     className="text-[#3a3a3a] leading-[1.85] text-sm md:text-base block">
                    {item.summary}
                  </EditableText>
                  {hasImage && (
                    <button
                      onClick={() => setZoomed(true)}
                      className="mt-5 self-start inline-flex items-center gap-1.5 text-xs font-medium text-[#7a2e2e] hover:text-[#5a1e1e] underline underline-offset-4"
                    >
                      <ZoomIn size={14} /> Read full clipping
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between px-6 pb-5 text-white">
            <button onClick={prev} aria-label="Previous clipping"
              className="flex items-center justify-center w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 transition-colors">
              <ChevronLeft size={20} />
            </button>
            <div className="flex items-center gap-1.5">
              {notableActivities.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-1.5 bg-white/40 hover:bg-white/60"}`}
                />
              ))}
            </div>
            <button onClick={next} aria-label="Next clipping"
              className="flex items-center justify-center w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 transition-colors">
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Zoom / lightbox */}
      <Dialog open={zoomed} onOpenChange={setZoomed}>
        <DialogContent className="max-w-5xl p-2 bg-[#1a1a1a] border-[#1a1a1a]">
          <button
            onClick={() => setZoomed(false)}
            aria-label="Close"
            className="absolute -top-3 -right-3 z-10 w-9 h-9 rounded-full bg-white text-black shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
          >
            <X size={18} />
          </button>
          {hasImage && (
            <img
              src={item.img}
              alt={item.title}
              className="w-full max-h-[85vh] object-contain rounded-sm"
              draggable={false}
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default NotableActivities;
